import React, { useState } from 'react';
import { Modal, Radio, Input, Button, Space, message } from 'antd';
import { declareApplication } from '../../services/api';
import { getApiErrorMessage, isConflictError } from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import css from './DeclareApplicationModal.module.scss';

const { TextArea } = Input;

type DeclaredChannel = 'referral' | 'direct';

interface DeclareApplicationModalProps {
  opportunityId: string | null;
  opportunityTitle: string;
  open: boolean;
  onClose: () => void;
  // Fired whenever the opportunity turns out to be APPLIED — after a 200, and
  // also after a 409 (already submitted/rejected); both mean "refresh the list".
  onSuccess: () => void;
}

const DeclareApplicationModal: React.FC<DeclareApplicationModalProps> = ({
  opportunityId,
  opportunityTitle,
  open,
  onClose,
  onSuccess,
}) => {
  const [channel, setChannel] = useState<DeclaredChannel | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setChannel(null);
    setNote('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!channel || !opportunityId) return;
    setLoading(true);
    try {
      await declareApplication(opportunityId, {
        channel,
        channelNote: channel === 'referral' && note.trim() ? note.trim() : undefined,
      });
      captureEvent('application_declared', {
        channel,
        has_note: channel === 'referral' && !!note.trim(),
      });
      message.success('Tracked — see it in Applications');
      handleClose();
      onSuccess();
    } catch (error) {
      if (isConflictError(error)) {
        // Declare is not idempotent; 409 means it's already submitted (or
        // rejected). Surface the backend's user-safe description and refresh.
        message.warning(getApiErrorMessage(error));
        handleClose();
        onSuccess();
      } else {
        message.error(getApiErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Track an application"
      width="min(520px, 94vw)"
      open={open}
      onCancel={handleClose}
      footer={null}
      className={css.declareModal}
    >
      <p className={css.subtitle}>
        How did you apply to "{opportunityTitle}"?
        <br />
        We won't send anything — this just tracks it.
      </p>

      {/* antd's RadioGroupProps doesn't type `role` (TS2322) even though the
          runtime would forward it, so the radiogroup semantics live on this
          wrapper — type-clean, no ts-expect-error. */}
      <div role="radiogroup" aria-label="How did you apply?">
        <Radio.Group
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className={css.radioGroup}
        >
          <Space direction="vertical" size="middle">
            <Radio value="referral">Through a referral — someone I know helped</Radio>
            <Radio value="direct">Directly myself — no referral</Radio>
          </Space>
        </Radio.Group>
      </div>

      {channel === 'referral' && (
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Who referred you? (optional)"
          rows={2}
          maxLength={500}
          className={css.textArea}
        />
      )}

      <div className={css.actions}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!channel}>
          Track it
        </Button>
      </div>
    </Modal>
  );
};

export default DeclareApplicationModal;
